<template>
  <div
    v-click-outside="onClickOutside"
    class="manufacturer-search-machine"
  >
    <div class="header-wrapper">
      <label class="label">
        Manufacturer
      </label>
      <span
        v-if="showRequired"
        class="required-label"
      >(required)</span>
      <router-link
        aria-label="Create New Manufacturer (opens in a new window)"
        to="/manufacturer/0"
        class="create-new"
        target="_blank"
      >
        Create
      </router-link>
    </div>
    <CloseSvg
      v-if="modelValue && modelValue !== ''"
      class="close"
      @click="onClearClicked"
    />
    <TextInput
      v-if="modelValue && modelValue !== ''"
      v-model="manufacturerName"
      :disabled="true"
    />
    <div
      v-else
      class="text-input-label"
    >
      <input
        v-model="searchTerm"
        class="text-input"
        aria-label="Search"
        :class="{'flatten-bottom': showResults}"
        placeholder="Search"
        autocomplete="off"
        :aria-required="showRequired"
        @input="onSearchTermChanged"
        @keyup.down="onDownArrowPressed"
        @keyup.up="onUpArrowPressed"
        @keyup.enter.stop="onEnterPressed"
        @keydown.tab="onClickOutside"
      >
      <div
        v-if="showResults"
        class="results-container"
      >
        <LoadingPulse
          :loading="searching"
          class="loading-spinner"
        />
        <div
          v-if="!searching"
          class="results"
        >
          <div
            v-for="result in searchResults"
            :key="result.id"
            class="result-option"
            :class="result.highlighted ? 'highlighted' : ''"
            @click="chooseOption(result)"
          >
            <div class="result-option-name">
              {{ result.name }}
            </div>
            <div class="result-option-email">
              {{ result.email }}
            </div>
            <div class="result-option-phone">
              {{ $methods.formatPhoneNumber(result.phoneNumber) }}
            </div>
          </div>
          <div
            v-if="searchResults.length == 0"
            class="no-results-container"
          >
            <div class="no-results">
              No results
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
/* eslint-disable @typescript-eslint/no-explicit-any */
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import { ManufacturerService } from '@/services/manufacturer.service'
import { defineComponent, inject } from 'vue'
import LoadingPulse from '@/components/shared/LoadingPulse.vue'
import { Manufacturer } from '@/@types/responses/manufacturer/Manufacturer'
import Toaster from '@meforma/vue-toaster'
import TextInput from '@/components/shared/TextInput.vue'
import CloseSvg from '@/components/svgs/CloseSvg.vue'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'

export default defineComponent({
  name: 'ManufacturerSearchMachine',
  components: {
    CloseSvg,
    LoadingPulse,
    TextInput
  },
  props: {
    modelValue: {
      type: String,
      required: true
    },
    showRequired: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue'],
  setup() {
    const manufacturerService = inject(PROVIDER_CONSTANTS.manufacturerService) as ManufacturerService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    function createDebounce() {
      let timeout: number|undefined = undefined
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      return function (func: any, delay?: number|undefined) {
        clearTimeout(timeout)
        timeout = setTimeout(() => { func() }, delay || 500)
      }
    }
    return {
      debounce: createDebounce(),
      manufacturerService,
      toast
    }
  },
  data() {
    let manufacturer: Manufacturer|undefined

    return {
      manufacturer,
      searching: false,
      searchTerm: '',
      searchResults: [] as Manufacturer[]
    }
  },
  computed: {
    manufacturerName(): string {
      if(!this.manufacturer) return ''

      return this.manufacturer.name
    },
    showResults(): boolean {
      return this.searchTerm !== '';
    }
  },
  mounted() {
    if (this.modelValue && this.modelValue !== '')
      this.manufacturerService.getManufacturer(Number(this.modelValue)).then(manufacturer => {
        this.manufacturer = new Manufacturer(manufacturer.id, manufacturer.name, manufacturer.phoneNumber, manufacturer.email, manufacturer.website)
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load your manufacturer. Try reloading the page.')
        }
      })
  },
  methods: {
    chooseOption(manufacturer: Manufacturer): void {
      this.searchTerm = ''
      this.manufacturer = manufacturer
      this.$emit('update:modelValue', manufacturer.id.toString())
    },
    getHighlightedManufacturer(): Manufacturer|undefined {
      const highlightedManufacturer = this.searchResults.filter(m => m.highlighted);
      return highlightedManufacturer.length > 0 ? highlightedManufacturer[0] : undefined;
    },
    onClearClicked(): void {
      this.$emit('update:modelValue', '')
    },
    onClickOutside(): void {
      this.searchTerm = ''
    },
    onDownArrowPressed(): void {
      const highlightedManufacturer = this.getHighlightedManufacturer();

      let nextIndex = highlightedManufacturer ? this.searchResults.indexOf(highlightedManufacturer) + 1 : 0;
      if (nextIndex > this.searchResults.length - 1)
        nextIndex = 0;
      
      if (highlightedManufacturer)
        highlightedManufacturer.highlighted = false;

      this.searchResults[nextIndex].highlighted = true;
    },
    onEnterPressed(): void {
      const highlightedManufacturer = this.getHighlightedManufacturer();
      if (highlightedManufacturer)
        this.chooseOption(highlightedManufacturer)
    },
    onSearchTermChanged(): void {
      if (this.searchTerm !== '') {
        this.searching = true
        this.debounce(this.searchManufacturers)
      }
    },
    onUpArrowPressed(): void {
      const highlightedManufacturer = this.getHighlightedManufacturer();
      let previousIndex = highlightedManufacturer ? this.searchResults.indexOf(highlightedManufacturer) - 1 : 0;

      if (previousIndex < 0)
        previousIndex = this.searchResults.length - 1;

      if (highlightedManufacturer)
        highlightedManufacturer.highlighted = false;

      this.searchResults[previousIndex].highlighted = true;
    },
    searchManufacturers(): void {
      this.manufacturerService.searchManufacturers(this.searchTerm, 1, 25, 'Name', true).then(pagingResponse => {
        this.searchResults = pagingResponse.results
        this.searching = false
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error(`Failed to search for your manufacturers. Please try again.`)
        }
      })
    }
  }
})
</script>

<style scoped lang="scss">
.manufacturer-search-machine {
  @include sac-search-machine;
  position: relative;

  > .close {
    position: absolute;
    right: 7px;
    top: 38px;
  }
}
</style>